import { useState, useMemo, useEffect } from 'react';
import { Movie, MovieFilters } from './types/movie';
import { getAllGenres } from './utils/movieUtils';
import { Header } from './components/Header';
import { MovieFilters as MovieFiltersComponent } from './components/MovieFilters';
import { MovieTable } from './components/MovieTable';
import { AddMovieForm } from './components/AddMovieForm';
import { MovieDetail } from './components/MovieDetail';
import { Statistics } from './components/Statistics';
import { LoginModal } from './components/LoginModal';
import { Plus, LogOut, User, ChevronLeft, ChevronRight } from 'lucide-react';
import { authService } from './services/authService';
import { getMovies, addMovie, deleteMovie as deleteMovieApi, updateMovieScore, getMovieStats, getAllMovies } from './services/movieApi';

function App() {
  const [movies, setMovies] = useState<Movie[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<MovieFilters>({
    search: '',
    scoreAbove7: false,
    genres: [],
    director: '',
    gf: false,
    sortBy: 'lastSeen',
    sortOrder: 'desc',
    page: 1,
    limit: 100
  });
  const [pagination, setPagination] = useState({
    totalCount: 0,
    currentPage: 1,
    totalPages: 1,
    limit: 100
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [selectedMovie, setSelectedMovie] = useState<Movie | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Load movies from API on component mount
  useEffect(() => {
    loadMovies();
  }, []);

  // Check authentication on app start
  useEffect(() => {
    const isLoggedIn = authService.isLoggedIn();
    setIsAuthenticated(isLoggedIn);
  }, []);

  const loadMovies = async (currentFilters?: MovieFilters) => {
    try {
      setLoading(true);
      setError(null);
      const filtersToUse = currentFilters || filters;
      const response = await getMovies(filtersToUse, true); // Force refresh to bypass cache
      setMovies(response.movies);
      setPagination({
        totalCount: response.totalCount,
        currentPage: response.currentPage,
        totalPages: response.totalPages,
        limit: response.limit
      });
    } catch (err) {
      setError('Failed to load movies');
      console.error('Error loading movies:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    setShowLoginModal(false);
  };

  const handleLogout = () => {
    authService.logout();
    setIsAuthenticated(false);
  };

  // Statistics state
  const [stats, setStats] = useState({
    totalMovies: 0,
    averageScore: 0,
    moviesWithGF: 0,
    moviesAbove7: 0,
    topGenres: [] as { genre: string; count: number }[],
    topDirectors: [] as { director: string; count: number }[]
  });

  // Calculate available genres and directors (we'll need to fetch all movies for this)
  const [allMovies, setAllMovies] = useState<Movie[]>([]);
  const availableGenres = useMemo(() => getAllGenres(allMovies), [allMovies]);

  // Load statistics from dedicated endpoint
  const loadStats = async () => {
    try {
      const statsData = await getMovieStats();
      setStats(statsData);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  // Load all movies for filter options and statistics
  const loadAllMovies = async () => {
    try {
      // Load all movies by making multiple paginated requests
      const movies = await getAllMovies();
      setAllMovies(movies);
      console.log(`Loaded ${movies.length} movies for statistics and filters`);
    } catch (err) {
      console.error('Error loading all movies for filter options:', err);
    }
  };

  useEffect(() => {
    loadAllMovies();
  }, []);

  const handleSortChange = (field: string, order: 'asc' | 'desc') => {
    const newFilters = {
      ...filters,
      sortBy: field as any,
      sortOrder: order,
      page: 1 // Reset to first page when sorting changes
    };
    setFilters(newFilters);
    loadMovies(newFilters);
  };

  const handleFiltersChange = (newFilters: MovieFilters) => {
    const updatedFilters = {
      ...newFilters,
      page: 1 // Reset to first page when filters change
    };
    setFilters(updatedFilters);
    loadMovies(updatedFilters);
  };

  const handlePageChange = (page: number) => {
    const newFilters = {
      ...filters,
      page
    };
    setFilters(newFilters);
    loadMovies(newFilters);
  };

  const handleAddMovie = async (newMovie: Omit<Movie, 'id'>) => {
    try {
      const result = await addMovie(newMovie);
      console.log('Movie added successfully:', result);

      // Refresh everything derived from the movie list so counts/filters/table
      // reflect the new movie immediately.
      await Promise.all([loadMovies(), loadStats(), loadAllMovies()]);

      setShowAddForm(false);
      // Redirect straight to the newly added movie's page.
      setSelectedMovie({ ...newMovie, id: result.id });
    } catch (err) {
      console.error('Error adding movie:', err);
      setError('Failed to add movie');
    }
  };

  const handleMovieClick = (movie: Movie) => {
    setSelectedMovie(movie);
  };

  const handleCloseMovieDetail = () => {
    setSelectedMovie(null);
  };

  const handleDeleteMovie = async (movieId: number) => {
    try {
      await deleteMovieApi(movieId);
      await Promise.all([loadMovies(), loadStats(), loadAllMovies()]);
    } catch (err) {
      console.error('Error deleting movie:', err);
      setError('Failed to delete movie');
    }
  };

  const handleUpdateScore = async (movieId: number, newScore: number) => {
    try {
      // Update the movie score via API
      await updateMovieScore(movieId, newScore);
      await Promise.all([loadMovies(), loadStats(), loadAllMovies()]);
    } catch (err) {
      console.error('Error updating score:', err);
      setError('Failed to update score');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header stats={stats} onShowStats={() => setShowStats(true)} />
      
      <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Add Movie Button and Auth Controls */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 space-y-3 sm:space-y-0">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Movie Collection</h2>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
            {isAuthenticated ? (
              <>
                <button
                  onClick={() => setShowAddForm(true)}
                  className="btn-primary flex items-center justify-center space-x-2 w-full sm:w-auto"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add Movie</span>
                </button>
                <div className="flex items-center justify-center sm:justify-start space-x-2 text-sm text-gray-600">
                  <User className="h-4 w-4" />
                  <span>Admin</span>
                </div>
                <button
                  onClick={handleLogout}
                  className="flex items-center justify-center space-x-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors w-full sm:w-auto"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Logout</span>
                </button>
              </>
            ) : (
              <button
                onClick={() => setShowLoginModal(true)}
                className="btn-primary flex items-center justify-center space-x-2 w-full sm:w-auto"
              >
                <User className="h-4 w-4" />
                <span>Admin Login</span>
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <MovieFiltersComponent
          filters={filters}
          onFiltersChange={handleFiltersChange}
          availableGenres={availableGenres}
        />

        {/* Results Summary */}
        <div className="mb-3 sm:mb-4 text-sm text-gray-600">
          Showing {movies.length} of {pagination.totalCount} movies (Page {pagination.currentPage} of {pagination.totalPages})
        </div>

        {/* Loading and Error States */}
        {loading && (
          <div className="text-center py-8 sm:py-12">
            <div className="text-gray-500 text-base sm:text-lg">Loading movies...</div>
          </div>
        )}

        {error && (
          <div className="text-center py-8 sm:py-12">
            <div className="text-red-500 text-base sm:text-lg mb-3 sm:mb-4">{error}</div>
            <button
              onClick={() => loadMovies()}
              className="btn-primary"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Movie Table */}
        {!loading && !error && (
          <>
            <MovieTable
              movies={movies}
              onMovieClick={handleMovieClick}
              onDeleteMovie={handleDeleteMovie}
              onUpdateScore={handleUpdateScore}
              isAuthenticated={isAuthenticated}
              sortBy={filters.sortBy}
              sortOrder={filters.sortOrder}
              onSortChange={handleSortChange}
            />
            
            {/* Pagination Controls */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-center space-x-2 mt-6">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                  className="flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </button>
                
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.currentPage >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = pagination.currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                          pageNum === pagination.currentPage
                            ? 'bg-primary-600 text-white shadow-sm'
                            : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className="flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              </div>
            )}
          </>
        )}

        {/* Modals */}
        {showAddForm && isAuthenticated && (
          <AddMovieForm
            onAddMovie={handleAddMovie}
            onClose={() => setShowAddForm(false)}
          />
        )}

        {showStats && (
          <Statistics
            stats={stats}
            movies={allMovies}
            onClose={() => setShowStats(false)}
            onMovieClick={handleMovieClick}
            onDeleteMovie={isAuthenticated ? handleDeleteMovie : undefined}
            onUpdateScore={isAuthenticated ? handleUpdateScore : undefined}
          />
        )}

        {selectedMovie && (
          <MovieDetail
            movie={selectedMovie}
            onClose={handleCloseMovieDetail}
          />
        )}

        {showLoginModal && (
          <LoginModal
            onLoginSuccess={handleLoginSuccess}
            onClose={() => setShowLoginModal(false)}
          />
        )}
      </main>
    </div>
  );
}

export default App;
